// =============================================================================
// src/config/features.js
// Same on every branch. NOT from process.env.
// ZS-only values go in ./zs_features.js; zs() below applies them on ZS.
// =============================================================================

import { DEPLOYMENT } from './deployment';
import ZS_FEATURES from './zs_features';

const ZS = DEPLOYMENT === 'zs' ? ZS_FEATURES : {};
const zs = (name, value) =>
  Object.prototype.hasOwnProperty.call(ZS, name) ? ZS[name] : value;

// DEV MODE (/dev and /removedev URL triggers + security key gate)
// false → /dev URL trigger does nothing, SecurityGate always renders children
export const ENABLE_DEV_MODE = zs('ENABLE_DEV_MODE', false);

// SELF SIGNUP (users creating their own accounts without admin involvement)
// The /sign-up route is ALWAYS registered — backend is the real gatekeeper.
//
// true  → any unknown email is routed to /sign-up
// false → only pre-approved emails (check-identifier returns pre_approved: true)
//         are routed to /sign-up; all others see an error toast.
//         Admin pre-approves users via admin panel → POST /admin/new-user-access
//         Backend enforces 403 on all signup paths for non-pre-approved emails.
export const ENABLE_SELF_SIGNUP = zs('ENABLE_SELF_SIGNUP', false);

// LOGIN — PASSWORD (email + password sign-in)
// false → password field/flow hidden, login defaults straight to OTP
// true  → password sign-in available
export const ENABLE_LOGIN_PASSWORD = zs('ENABLE_LOGIN_PASSWORD', true);

// LOGIN — OTP (email one-time-code sign-in, existing users)
// false → "Sign in with one-time code" hidden, no switching to/from OTP
// true  → OTP sign-in available
export const ENABLE_LOGIN_OTP = zs('ENABLE_LOGIN_OTP', true);

// LOGIN — GOOGLE OAUTH
// false → Google button hidden on login page
// true  → Google login button available
export const ENABLE_LOGIN_GOOGLE = zs('ENABLE_LOGIN_GOOGLE', false);

// LOGIN — MICROSOFT OAUTH (Outlook-scoped, mail/integration permissions)
// false → Microsoft button hidden on login page
// true  → Microsoft login button available
export const ENABLE_LOGIN_MICROSOFT = zs('ENABLE_LOGIN_MICROSOFT', true);

// LOGIN — MICROSOFT ENTERPRISE / AZURE AD SSO (identity-only, tenant-lockable)
// false → Enterprise/Azure AD section + silent SSO check hidden
// true  → Enterprise/Azure AD login available
export const ENABLE_LOGIN_MICROSOFT_SSO = zs(
  'ENABLE_LOGIN_MICROSOFT_SSO',
  true,
);

// LOGIN — LINKEDIN OAUTH
// false → LinkedIn button hidden on login page
// true  → LinkedIn login button available
export const ENABLE_LOGIN_LINKEDIN = zs('ENABLE_LOGIN_LINKEDIN', false);

// AI CHAT (full AI chat pipeline)
// false → /chat and /chat/:taskId routes redirect to /communication (next tab)
//          AgentProvider not mounted
//          TaskPlanProvider not mounted
//          onProjectAccess navigates to /communication instead of /chat
//          root / redirects to /communication instead of /chat
// true  → full AI chat UI available
export const ENABLE_AI_CHAT = zs('ENABLE_AI_CHAT', true);

// AGENT MODE (multi-agent orchestration in chat; needs ENABLE_AI_CHAT)
// false → Agent button, /agent command and Agent Architecture modal hidden;
//          agent info is not loaded
// true  → Agent mode available in chat
export const ENABLE_AGENT_MODE = zs('ENABLE_AGENT_MODE', false);

// PROJECT REGISTER / ANALYTICS (Deliverables, Gantt insights, commitments, risks)
// false → Analytics tab hidden from top tab bar
// true  → full Project Register UI available in top bar
export const ENABLE_PROJECT_REGISTER = zs('ENABLE_PROJECT_REGISTER', true);

// PROMPT INPUT — WEB SEARCH BUTTON (the "Web" toggle in the chat prompt toolbar)
// Pulled off the toolbar for now pending improvements; the underlying
// isWebSearch state/toggle logic in AutoResizableTextarea is untouched so this
// is a one-line flip to bring back.
// false → "Web" button hidden from both the desktop and mobile toolbars
// true  → "Web" button available
export const ENABLE_PROMPT_WEB_SEARCH_BUTTON = zs(
  'ENABLE_PROMPT_WEB_SEARCH_BUTTON',
  false,
);

// COMMUNICATION (email / communication tab)
// No backend change needed — email APIs live inside task_routers.
// false → /communication route redirects to /workflow (next tab)
//          Communication tab hidden from top tab bar
//          EmailsProvider not mounted
// true  → full Communication UI available
export const ENABLE_COMMUNICATION = zs('ENABLE_COMMUNICATION', false);

// RIGHT SIDEBAR COMMUNICATION (emails tab + morning briefing inside the chat sidebar)
// Independent of ENABLE_COMMUNICATION — controls only the right panel in /chat.
// false → Emails tab hidden from right sidebar, Morning Briefing button hidden
// true  → Emails tab and Morning Briefing available in the chat right sidebar
export const ENABLE_RIGHT_SIDEBAR_COMMUNICATION = zs(
  'ENABLE_RIGHT_SIDEBAR_COMMUNICATION',
  true,
);

// MCP (Model Context Protocol — tool connections + global connections)
// false → McpProvider not mounted
// true  → full MCP context available
export const ENABLE_MCP = zs('ENABLE_MCP', false);

// MEETINGS (meeting detail page + meeting summary page)
// false → /meeting and /meeting-summary/:eventId routes not rendered (404 if accessed directly)
// true  → meeting pages available
export const ENABLE_MEETINGS = zs('ENABLE_MEETINGS', true);

// MEETING APPROVALS PAGE (/meeting-approvals: tracker follow-up proposals and
// clarifications across every meeting)
// false → the page is not rendered (not tested yet; the meeting summary's own
//          review tabs still work)
// true  → page available
export const ENABLE_MEETING_APPROVALS_PAGE = zs(
  'ENABLE_MEETING_APPROVALS_PAGE',
  false,
);

// SHAREPOINT (SharePoint document history + diff viewer + test page)
// false → /sharepoint-test and /document-history routes hidden
//          Document History tab hidden in Profile Settings
// true  → full SharePoint document pipeline available
export const ENABLE_SHAREPOINT = zs('ENABLE_SHAREPOINT', true);

// REPORT TEMPLATES (report template builder + @template chat mention)
// false → /report-templates route hidden, Report Templates sidebar button hidden
//          @template and @ triggers removed from chat mention system
// true  → full report templates pipeline available
export const ENABLE_REPORT_TEMPLATES = zs('ENABLE_REPORT_TEMPLATES', true);

// DASHBOARDS (chat-built dashboards: a JSON spec rendered live, editable, shareable)
// Mirrors backend app/config/features.py. See docs/DASHBOARD_BUILDER.md.
// false → /dashboards routes hidden, dashboard cards not shown in chat
// true  → dashboards can be opened, edited and shared
export const ENABLE_DASHBOARDS = zs('ENABLE_DASHBOARDS', true);

// SKILLS (DB-backed agent skills — system-wide + project-uploaded)
// false → Skills tab hidden in Profile Settings
// true  → users can upload/manage their own project-scoped skills
// Must stay in lockstep with the backend's ENABLE_SKILLS flag
// (app/config/features.py) — a mismatch just means requests to
// /data/skills/* 404 with the tab visible, so flip both together.
export const ENABLE_SKILLS = zs('ENABLE_SKILLS', true);

// MAIL SIGNATURE (the user's own signature on mail sent from chat)
// false → Mail tab hidden in Profile Settings; approval card shows no signature
// true  → the backend appends the auto-detected signature to every mail sent
//          from chat; the approval card previews it with an opt-out
// Must stay in lockstep with the backend's ENABLE_MAIL_SIGNATURE flag
// (app/config/features.py) — flip both together.
export const ENABLE_MAIL_SIGNATURE = zs('ENABLE_MAIL_SIGNATURE', true);

// MAIL TONE (internal vs external mail)
// false → no audience badge or internal-content warnings on the approval card;
//          no tone settings in Settings > Mail
// true  → the approval card shows Internal / External and warns when an
//          external mail mentions internal tickets or links
// Must stay in lockstep with the backend's ENABLE_MAIL_TONE flag.
export const ENABLE_MAIL_TONE = zs('ENABLE_MAIL_TONE', true);

// MAIL INTELLIGENCE (project mail triage; Phase 0 shadow, internal only)
// false → Mail Intelligence tab hidden in Profile Settings
// true  → Settings > Mail Intelligence: connect own mailbox, answer the
//          weekly "was this project mail?" gate audit
// Must stay in lockstep with the backend's ENABLE_MAIL_INTEL flag.
export const ENABLE_MAIL_INTEL = zs('ENABLE_MAIL_INTEL', true);

// TEAMS DIRECT MESSAGES (the member's own Teams 1:1 chats in the chat sidebar)
// false → Chats tab hidden from the right sidebar
// true  → Chats tab lists the member's own chats, offers a suggested reply;
//          Reply fills the box and Send posts it as the member
// Must stay in lockstep with the backend's ENABLE_TEAMS_DM flag.
export const ENABLE_TEAMS_DM = zs('ENABLE_TEAMS_DM', true);

// AGENT QUESTIONS (the Waiting tab's 1:1 chat with the agent)
// false → Waiting shows meetings only
// true  → open meeting and mail questions sit on top of Waiting; each opens
//          a chat where a reply that answers it is recorded
// Must stay in lockstep with the backend's ENABLE_AGENT_QUESTIONS flag.
export const ENABLE_AGENT_QUESTIONS = zs('ENABLE_AGENT_QUESTIONS', true);

// Teams channels in the Chats tab (list, read, post). Must stay in lockstep
// with the backend's ENABLE_TEAMS_WORKSPACE_TOOLS flag (channel routes and scopes).
export const ENABLE_TEAMS_CHANNELS = zs('ENABLE_TEAMS_CHANNELS', true);

// USER MEMORY (what the assistant remembers across chats)
// false → Memory tab hidden in Profile Settings
// true  → Settings > Memory lists everything remembered, with delete
// Must stay in lockstep with the backend's ENABLE_USER_MEMORY flag.
export const ENABLE_USER_MEMORY = zs('ENABLE_USER_MEMORY', true);

// USER MANAGEMENT (team user management panel)
// false → User Management tab hidden in Profile Settings
//          /users/* endpoints gated on backend
// true  → full user management available
export const ENABLE_USER_MANAGEMENT = zs('ENABLE_USER_MANAGEMENT', true);

// FEEDBACK ("Quick chat with team" modal — feedback submission to Supermanager team)
// false → Feedback button hidden in sidebar, modal not rendered
// true  → feedback modal available
export const ENABLE_FEEDBACK = zs('ENABLE_FEEDBACK', false);

// WORKFLOWS (workflow builder + execution tab)
// false → /workflow and /workflow-canvas routes hidden
//          Workflow tab hidden from top tab bar
//          WorkflowExecutionsProvider not mounted
// true  → full workflow UI available
export const ENABLE_WORKFLOWS = zs('ENABLE_WORKFLOWS', false);

// GMAIL INTEGRATION (connecting Gmail as a tool — reading/sending emails, webhooks)
// false → Gmail hidden from integrationData, Integration.js, MailBoxesIntegration,
//           slash commands, email app list, and Ui.js notification handler
// true  → full Gmail integration UI available
export const ENABLE_INTEGRATION_GMAIL = zs('ENABLE_INTEGRATION_GMAIL', false);

// GOOGLE CALENDAR INTEGRATION (connecting Google Calendar as a tool)
// false → Google Calendar hidden from integrationData, authApps, slash commands, Integration.js
// true  → full Google Calendar integration UI available
export const ENABLE_INTEGRATION_GOOGLE_CALENDAR = zs(
  'ENABLE_INTEGRATION_GOOGLE_CALENDAR',
  false,
);

// GOOGLE DRIVE INTEGRATION (connecting Google Drive as a tool)
// false → Google Drive hidden from integrationData, authApps, slash commands, Integration.js, and DataSourceModals
// true  → full Google Drive integration UI available
export const ENABLE_INTEGRATION_GOOGLE_DRIVE = zs(
  'ENABLE_INTEGRATION_GOOGLE_DRIVE',
  false,
);

// GOOGLE DOCS INTEGRATION (connecting Google Docs as a tool)
// false → Google Docs hidden from integrationData, authApps, slash commands, and Integration.js
// true  → full Google Docs integration UI available
export const ENABLE_INTEGRATION_GOOGLE_DOCS = zs(
  'ENABLE_INTEGRATION_GOOGLE_DOCS',
  false,
);

// GOOGLE SHEETS INTEGRATION (connecting Google Sheets as a tool)
// false → Google Sheets hidden from integrationData, authApps, triggerData, slash commands, and Integration.js
// true  → full Google Sheets integration UI available
export const ENABLE_INTEGRATION_GOOGLE_SHEETS = zs(
  'ENABLE_INTEGRATION_GOOGLE_SHEETS',
  false,
);

// GOOGLE FORMS INTEGRATION (connecting Google Forms as a tool)
// false → Google Forms hidden from integrationData, authApps, triggerData, slash commands, and Integration.js
// true  → full Google Forms integration UI available
export const ENABLE_INTEGRATION_GOOGLE_FORMS = zs(
  'ENABLE_INTEGRATION_GOOGLE_FORMS',
  false,
);

// GOOGLE KEEP INTEGRATION (connecting Google Keep as a tool)
// false → Google Keep hidden from authApps, GroupsModal, and Integration.js
// true  → full Google Keep integration UI available
export const ENABLE_INTEGRATION_GOOGLE_KEEP = zs(
  'ENABLE_INTEGRATION_GOOGLE_KEEP',
  false,
);

// GOOGLE TASKS INTEGRATION (connecting Google Tasks as a tool)
// false → Google Tasks hidden from integrationData, authApps, triggerData, GroupsModal, slash commands, and Integration.js
// true  → full Google Tasks integration UI available
export const ENABLE_INTEGRATION_GOOGLE_TASKS = zs(
  'ENABLE_INTEGRATION_GOOGLE_TASKS',
  false,
);

// GOOGLE DIRECTORY INTEGRATION (connecting Google Directory as a tool)
// false → Google Directory hidden from authApps
// true  → full Google Directory integration UI available
export const ENABLE_INTEGRATION_GOOGLE_DIRECTORY = zs(
  'ENABLE_INTEGRATION_GOOGLE_DIRECTORY',
  false,
);

// GOOGLE VISION INTEGRATION (connecting Google Vision as a tool)
// false → Google Vision hidden/disabled
// true  → full Google Vision integration available
export const ENABLE_INTEGRATION_GOOGLE_VISION = zs(
  'ENABLE_INTEGRATION_GOOGLE_VISION',
  false,
);

// YOUTUBE INTEGRATION (connecting YouTube as a tool)
// false → YouTube hidden from integrationData, authApps, slash commands, and Integration.js
// true  → full YouTube integration UI available
export const ENABLE_INTEGRATION_YOUTUBE = zs(
  'ENABLE_INTEGRATION_YOUTUBE',
  false,
);

// MICROSOFT OUTLOOK INTEGRATION (connecting Microsoft Outlook as a tool)
// false → Microsoft Outlook hidden from integrationData, authApps, triggerData, slash commands
// true  → full Microsoft Outlook integration UI available
export const ENABLE_INTEGRATION_MICROSOFT_OUTLOOK = zs(
  'ENABLE_INTEGRATION_MICROSOFT_OUTLOOK',
  true,
);

// MICROSOFT TEAMS INTEGRATION (connecting Microsoft Teams as a tool)
// false → Microsoft Teams hidden from integrationData, authApps, triggerData, slash commands
// true  → full Microsoft Teams integration UI available
export const ENABLE_INTEGRATION_MICROSOFT_TEAMS = zs(
  'ENABLE_INTEGRATION_MICROSOFT_TEAMS',
  true,
);

// MICROSOFT EXCEL INTEGRATION (connecting Microsoft Excel as a tool)
// false → Microsoft Excel hidden from authApps
// true  → full Microsoft Excel integration UI available
export const ENABLE_INTEGRATION_MICROSOFT_EXCEL = zs(
  'ENABLE_INTEGRATION_MICROSOFT_EXCEL',
  false,
);

// MICROSOFT ONEDRIVE INTEGRATION (connecting Microsoft OneDrive as a tool)
// false → Microsoft OneDrive hidden from integrationData, authApps, slash commands
// true  → full Microsoft OneDrive integration UI available
export const ENABLE_INTEGRATION_MICROSOFT_ONEDRIVE = zs(
  'ENABLE_INTEGRATION_MICROSOFT_ONEDRIVE',
  false,
);

// MICROSOFT WORD INTEGRATION (connecting Microsoft Word as a tool)
// false → Microsoft Word hidden from authApps
// true  → full Microsoft Word integration UI available
export const ENABLE_INTEGRATION_MICROSOFT_WORD = zs(
  'ENABLE_INTEGRATION_MICROSOFT_WORD',
  false,
);

// MICROSOFT ONENOTE INTEGRATION (connecting Microsoft OneNote as a tool)
// false → Microsoft OneNote hidden from authApps
// true  → full Microsoft OneNote integration UI available
export const ENABLE_INTEGRATION_MICROSOFT_ONENOTE = zs(
  'ENABLE_INTEGRATION_MICROSOFT_ONENOTE',
  false,
);

// MICROSOFT SHAREPOINT INTEGRATION (approved SharePoint sites, optional OneDrive)
// false → SharePoint hidden from Integrations
// true  → SharePoint can be connected; access choices shown before sign-in
export const ENABLE_INTEGRATION_MICROSOFT_SHAREPOINT = zs(
  'ENABLE_INTEGRATION_MICROSOFT_SHAREPOINT',
  true,
);

// MICROSOFT TODO INTEGRATION (connecting Microsoft To Do as a tool)
// false → Microsoft To Do hidden from integrationData, authApps, slash commands
// true  → full Microsoft To Do integration UI available
export const ENABLE_INTEGRATION_MICROSOFT_TODO = zs(
  'ENABLE_INTEGRATION_MICROSOFT_TODO',
  true,
);

// AZURE DEVOPS INTEGRATION (connecting Azure DevOps as a tool)
// false → Azure DevOps hidden from integrationData, authApps, slash commands
// true  → full Azure DevOps integration UI available
export const ENABLE_INTEGRATION_AZURE_DEVOPS = zs(
  'ENABLE_INTEGRATION_AZURE_DEVOPS',
  false,
);

// JIRA INTEGRATION (connecting Jira as a tool)
// false → Jira hidden from integrationData, authApps, triggerData, slash commands
// true  → full Jira integration UI available
export const ENABLE_INTEGRATION_JIRA = zs('ENABLE_INTEGRATION_JIRA', true);

// CONFLUENCE INTEGRATION (connecting Confluence as a tool)
// false → Confluence hidden from integrationData, authApps, triggerData, slash commands
// true  → full Confluence integration UI available
export const ENABLE_INTEGRATION_CONFLUENCE = zs(
  'ENABLE_INTEGRATION_CONFLUENCE',
  true,
);

// LINEAR INTEGRATION (connecting Linear as a tool)
// false → Linear hidden from integrationData, authApps, slash commands
// true  → full Linear integration UI available
export const ENABLE_INTEGRATION_LINEAR = zs('ENABLE_INTEGRATION_LINEAR', false);

// ASANA INTEGRATION (connecting Asana as a tool)
// false → Asana hidden from integrationData, authApps, slash commands
// true  → full Asana integration UI available
export const ENABLE_INTEGRATION_ASANA = zs('ENABLE_INTEGRATION_ASANA', false);

// NOTION INTEGRATION (connecting Notion as a tool)
// false → Notion hidden from integrationData, authApps, slash commands
// true  → full Notion integration UI available
export const ENABLE_INTEGRATION_NOTION = zs('ENABLE_INTEGRATION_NOTION', false);

// WRIKE INTEGRATION (connecting Wrike as a tool)
// false → Wrike hidden from integrationData, authApps, slash commands
// true  → full Wrike integration UI available
export const ENABLE_INTEGRATION_WRIKE = zs('ENABLE_INTEGRATION_WRIKE', false);

// GITLAB INTEGRATION (connecting GitLab as a tool)
// false → GitLab hidden from integrationData, authApps, slash commands
// true  → full GitLab integration UI available
export const ENABLE_INTEGRATION_GITLAB = zs('ENABLE_INTEGRATION_GITLAB', false);

// PIPEDRIVE INTEGRATION (connecting Pipedrive as a tool)
// false → Pipedrive hidden from integrationData, authApps, slash commands
// true  → full Pipedrive integration UI available
export const ENABLE_INTEGRATION_PIPEDRIVE = zs(
  'ENABLE_INTEGRATION_PIPEDRIVE',
  false,
);

// SLACK INTEGRATION (connecting Slack as a tool)
// false → Slack hidden from integrationData, authApps, slash commands
// true  → full Slack integration UI available
export const ENABLE_INTEGRATION_SLACK = zs('ENABLE_INTEGRATION_SLACK', false);

// GITHUB INTEGRATION (connecting GitHub as a tool)
// false → GitHub hidden from integrationData, authApps, slash commands
// true  → full GitHub integration UI available
export const ENABLE_INTEGRATION_GITHUB = zs('ENABLE_INTEGRATION_GITHUB', false);

// ZOHO CRM INTEGRATION (connecting Zoho CRM as a tool)
// false → Zoho CRM hidden from integrationData, authApps, slash commands
// true  → full Zoho CRM integration UI available
export const ENABLE_INTEGRATION_ZOHO_CRM = zs(
  'ENABLE_INTEGRATION_ZOHO_CRM',
  false,
);

// GOHIGHLEVEL INTEGRATION (connecting GoHighLevel as a tool)
// false → GoHighLevel hidden from integrationData, authApps, slash commands
// true  → full GoHighLevel integration UI available
export const ENABLE_INTEGRATION_GOHIGHLEVEL = zs(
  'ENABLE_INTEGRATION_GOHIGHLEVEL',
  false,
);

// HUBSPOT INTEGRATION (connecting HubSpot as a tool)
// false → HubSpot hidden from integrationData, authApps, slash commands
// true  → full HubSpot integration UI available
export const ENABLE_INTEGRATION_HUBSPOT = zs(
  'ENABLE_INTEGRATION_HUBSPOT',
  false,
);

// INSTAGRAM INTEGRATION (connecting Instagram as a tool)
// false → Instagram hidden from integrationData, authApps, slash commands
// true  → full Instagram integration UI available
export const ENABLE_INTEGRATION_INSTAGRAM = zs(
  'ENABLE_INTEGRATION_INSTAGRAM',
  false,
);

// QUICKBOOKS INTEGRATION (connecting QuickBooks as a tool)
// false → QuickBooks hidden from integrationData, authApps, slash commands
// true  → full QuickBooks integration UI available
export const ENABLE_INTEGRATION_QUICKBOOKS = zs(
  'ENABLE_INTEGRATION_QUICKBOOKS',
  false,
);

// RAZORPAY INTEGRATION (connecting Razorpay as a tool)
// false → Razorpay hidden from integrationData, authApps, slash commands
// true  → full Razorpay integration UI available
export const ENABLE_INTEGRATION_RAZORPAY = zs(
  'ENABLE_INTEGRATION_RAZORPAY',
  false,
);

// SHIPROCKET INTEGRATION (connecting Shiprocket as a tool)
// false → Shiprocket hidden from integrationData, authApps, slash commands
// true  → full Shiprocket integration UI available
export const ENABLE_INTEGRATION_SHIPROCKET = zs(
  'ENABLE_INTEGRATION_SHIPROCKET',
  false,
);

// ZENDESK INTEGRATION (connecting Zendesk as a tool)
// false → Zendesk hidden from integrationData, authApps, slash commands
// true  → full Zendesk integration UI available
// TODO: Zendesk is not yet available in the integration modal (missing from integrationData).
// TODO: Zendesk subdomain is hardcoded in the backend .env — single-tenant only.
export const ENABLE_INTEGRATION_ZENDESK = zs(
  'ENABLE_INTEGRATION_ZENDESK',
  false,
);

// ADMIN SECTION (admin panel and routes)
// false → Admin section hidden and routes disabled
// true  → Admin section available
export const ENABLE_ADMIN_SECTION = zs('ENABLE_ADMIN_SECTION', true);

// SECOND APPROVAL FOR SENSITIVE SETTINGS — mirror of the backend flag. The
// admin page reads the server's answer (require_second_approval); this copy
// only keeps the two files in sync.
export const REQUIRE_SECOND_APPROVAL = zs('REQUIRE_SECOND_APPROVAL', false);

// ADMIN USERS SECTION (users tab in admin panel)
// false → Users tab hidden
// true  → Users tab available
export const ENABLE_ADMIN_USERS_SECTION = zs(
  'ENABLE_ADMIN_USERS_SECTION',
  true,
);

// ADMIN COST ACTION (View Costs button and modal in Admin Users Table)
// false → Cost action hidden
// true  → Cost action available
export const ENABLE_ADMIN_COST_ACTION = zs('ENABLE_ADMIN_COST_ACTION', false);

// ADMIN ROLES SECTION (Roles & Permissions tab and Roles button in Admin Panel)
// false → Admin roles section hidden (project roles come from the member type,
//          Super Admin from the backend's INITIAL_ADMIN_EMAILS)
// true  → Admin roles section available
export const ENABLE_ADMIN_ROLES_SECTION = zs(
  'ENABLE_ADMIN_ROLES_SECTION',
  false,
);

// ADMIN ACTIVITY LOGS SECTION (Activity Logs tab in Admin Panel)
// false → Admin activity logs section hidden
// true  → Admin activity logs section available
export const ENABLE_ADMIN_ACTIVITY_LOGS = zs(
  'ENABLE_ADMIN_ACTIVITY_LOGS',
  true,
);
